import * as React from 'react'

import { makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import { PermitFieldType } from '~/client/graph/types.generated'
import { materialPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import InitialState from '~/client/src/shared/stores/InitialState'

import PermitDatePicker from '../../../PermitDatePicker/PermitDatePicker'
import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import SignatureModal from '../PermitFormFields/components/SignatureModal/SignatureModal'
import ResourceRestrictionsSync from '../ResourceRestrictionsSync'
import FormDropdownOptionsModal from './components/FormDropdownOptionsModal/FormDropdownOptionsModal'

// localization: no text to translate

interface IProps {
  store: SitePermitCreationFormStore
  isPreviewMode: boolean
  state?: InitialState
  allowedTypeIds?: string[]
  // The composer hosts pickers in a drawer that unmounts on close, so it mounts
  // ResourceRestrictionsSync itself (form-scoped) and opts out here to avoid a
  // second sync that would clear the store's resource maps on picker close.
  skipResourceSync?: boolean
}

const DROPDOWN_TYPES = [
  ...materialPermitFieldTypes,
  PermitFieldType.Select,
  PermitFieldType.Measure,
]

@inject('state')
@observer
export default class PermitContentPickers extends React.Component<IProps> {
  @observable private store: SitePermitCreationFormStore = null

  public constructor(props: IProps) {
    super(props)

    this.store = props.store
    makeObservable(this)
  }

  public render() {
    const { isDatePickerDisplayed, resetSelectedField } = this.store

    return (
      <>
        {/* Always mounted for the form's lifetime so fresh resource
            permittedCompanies and controls are loaded at save time, not only
            while a picker modal is open. The composer opts out (skipResourceSync)
            and mounts its own at a stable spot, since its drawer unmounts. */}
        {!this.props.skipResourceSync && (
          <ResourceRestrictionsSync
            store={this.store}
            projectId={this.props.state?.activeProject?.id}
          />
        )}
        {this.renderContent(isDatePickerDisplayed, resetSelectedField)}
      </>
    )
  }

  private renderContent(
    isDatePickerDisplayed: boolean,
    resetSelectedField: () => void,
  ) {
    if (isDatePickerDisplayed) {
      const {
        datePickerStart,
        datePickerEnd,
        shouldShowTimePicker,
        isOneDayMode,
        maxDateRangeInYears,
        isAllDay,
        workflowFieldsStore,
      } = this.store

      const { formTypeMaxDuration, maxDurationMessage } = workflowFieldsStore

      return (
        <PermitDatePicker
          applyDates={this.onApplyDates}
          hideDatePicker={resetSelectedField}
          initialStartDate={datePickerStart}
          initialEndDate={datePickerEnd}
          initialIsAllDay={isAllDay}
          shouldShowTimePicker={shouldShowTimePicker}
          isOneDayMode={isOneDayMode}
          maxRangeInYears={maxDateRangeInYears}
          maxDurationType={formTypeMaxDuration.maxDurationType}
          maxDuration={formTypeMaxDuration.maxDuration}
          maxDurationMessage={maxDurationMessage}
        />
      )
    }

    return (
      <>
        <FormDropdownOptionsModal
          store={this.store}
          isShown={this.isShown}
          isPreviewMode={this.props.isPreviewMode}
          allowedTypeIds={this.props.allowedTypeIds}
        />
        <SignatureModal
          isShown={this.store.isSignatureModalShown}
          userId={this.currentUserId}
          initialSignature={this.store.signatureInitialValue}
          onConfirm={this.store.confirmSignature}
          onCancel={this.store.hideSignatureModal}
        />
      </>
    )
  }

  private get currentUserId(): string {
    return this.props.state?.user?.id || ''
  }

  private onApplyDates = (
    newStartDate: Date,
    newEndDate: Date,
    isAllDay: boolean,
  ) => {
    const { isPermitDatesFieldSelected, applyDates, changeSelectedFieldValue } =
      this.props.store

    if (isPermitDatesFieldSelected) {
      applyDates(newStartDate, newEndDate, isAllDay)
    } else {
      changeSelectedFieldValue(newStartDate.getTime())
    }
  }

  private get isShown(): boolean {
    return (
      this.store.isDatePickerDisplayed ||
      this.store.isTypeSelectorShown ||
      this.store.isEquipmentPickerDisplayed ||
      this.store.isMaterialResourcePickerDisplayed ||
      this.store.isLocationPickerDisplayed ||
      this.store.isCompanyPickerDisplayed ||
      this.store.isUserPickerDisplayed ||
      this.store.isWorkflowPickerDisplayed ||
      DROPDOWN_TYPES.includes(this.store.selectedField?.type)
    )
  }
}
